/**
 * Application orchestrator.
 *
 * Coordinates Renderer + World + Color + Typography + Capture + UI against
 * the central store. Blobs remain independent particles for their lifetime;
 * visual unification is renderer-only via a chain smin (per-pair k computed
 * CPU-side in the World).
 *
 * Coordinate convention:
 *   - World/physics: pixels, origin at canvas CENTER, +y up.
 *   - Shader space: short-edge spans [-1, +1].
 *
 * Color assignment for new blobs: app.ts holds a current channel pair (A, B)
 * and alternates A→B→A as new blobs are added. Reset re-rolls the pair.
 *
 * Performance hygiene: the hotspot payload is rebuilt only when the world's
 * blob list mutates (tracked via World.version), not every frame.
 */
import { SpawnSound } from './audio/sound.js';
import {
  applyAspect,
  computeViewport,
  type AspectAdapter,
} from './capture/aspect.js';
import {
  createPngExporter,
  downloadBlob,
  type PngExporter,
  type RenderAdapter,
  type TypographyAdapter,
} from './capture/png.js';
import { createWebmRecorder, type WebmRecorder } from './capture/webm.js';
import { CHANNELS } from './color/channels.js';
import {
  generateBlobColorPair,
  serializeForShader,
  type BlobColorPairResult,
  type ShaderColorPayload,
} from './color/gradient.js';
import { World, type PhysicsParams } from './physics/world.js';
import type { HotspotPayload, RendererUpdateState } from './render/renderer.js';
import { Renderer } from './render/renderer.js';
import { store, type AppState } from './state.js';
import {
  composeTypography,
  ensureFontsReady,
  renderTypographyToCanvas,
} from './typography/compose.js';
import { pickCorners } from './typography/layout.js';
import type {
  AspectId,
  BlobColor,
  ChannelId,
  TextLayout,
} from './types.js';
import { mountControls, type ControlActions, type ControlsHandle } from './ui/controls.js';
import { generateSeed, rngFromSeed } from './utils/seed.js';
import {
  installWakeLockVisibilityHandler,
  requestWakeLock,
} from './utils/wake-lock.js';

const BACKGROUND_COLOR_SRGB: [number, number, number] = [
  0xfa / 255,
  0xfa / 255,
  0xf7 / 255,
];

export class App {
  private readonly canvas: HTMLCanvasElement;
  private readonly overlay: HTMLElement;
  private readonly controlsRoot: HTMLElement;

  private readonly renderer: Renderer;
  private readonly world: World;
  private readonly pngExporter: PngExporter;
  /**
   * Rebuilt at every recording start so the live `audioStreams`
   * (Tone master + current bgMedia video, if any) are wired into
   * the new MediaRecorder. Null between sessions.
   */
  private webmRecorder: WebmRecorder | null = null;
  private readonly spawnSound: SpawnSound;
  private controlsHandle: ControlsHandle | null = null;

  private layout: TextLayout = pickCorners(store.get().seed, store.get().aspect);

  /** Current channel color pair. New blobs alternate between these two. */
  private blobColorA: BlobColor;
  private blobColorB: BlobColor;
  /** Index used to alternate A/B as new blobs are added. */
  private nextColorPick = 0;

  /** Cached hotspot payload, rebuilt when world.version changes. */
  private hotspotPayload: ShaderColorPayload;
  private payloadVersion = -1;
  private hotspotPayloadView: HotspotPayload;

  /** Mouse drag state — by blob id (stable across reorderings). */
  private dragBlobId: string | null = null;
  private prevMouseWorld: [number, number] | null = null;

  /**
   * Phase 5b — pairs (sorted blob-id keys "idA|idB") that are
   * currently inside the fusion-chord contact zone. Used by
   * `detectFusionEvents()` to debounce the chord trigger so each
   * pair fires once per contact event, then re-arms only after the
   * pair separates and re-collides.
   */
  private mergedPairs = new Set<string>();

  private rafHandle: number | null = null;
  private startTime = 0;
  private prevFrameMs = 0;

  private typographyMounted = false;

  /**
   * Phase 8 — recording composite layer. The WebGL canvas is blitted
   * here each frame during recording, then the typography overlay (a
   * cached transparent 2D canvas) is drawn on top. `captureStream`
   * runs against this canvas so the WebM mux includes the type, which
   * the bare WebGL stream cannot. Lazily created on first recording
   * start; size tracks the WebGL canvas's intrinsic size and is
   * updated inside `updateCompositeCanvasSize` (called from the tick
   * loop while recording).
   */
  private compositeCanvas: HTMLCanvasElement | null = null;
  private compositeCtx: CanvasRenderingContext2D | null = null;
  /**
   * Cached typography canvas used by the recording composite path.
   * Rendered once per (size + text + orientation + layout + title-typo)
   * change via `ensureTypographyOverlayCanvas()` and reused every frame
   * while recording. `null` = invalidated, will be re-rendered on next
   * frame demand. The async render kicks off when the slot is null and
   * a frame is requested; until it lands the overlay is skipped (no
   * stale text) so the first ~1 frame of a fresh layout records as
   * blob-only.
   */
  private typographyOverlayCanvas: HTMLCanvasElement | null = null;
  /** Inflight render guard so we don't spam concurrent rasterisations. */
  private typographyOverlayRenderInflight = false;

  constructor(opts: {
    canvas: HTMLCanvasElement;
    overlay: HTMLElement;
    controls: HTMLElement;
  }) {
    this.canvas = opts.canvas;
    this.overlay = opts.overlay;
    this.controlsRoot = opts.controls;

    this.renderer = new Renderer(this.canvas);

    const initial = store.get();
    const params = paramsFromState(initial);
    const initialBounds = { width: 1920, height: 1080 };
    this.world = new World(params, initialBounds, rngFromSeed(initial.seed));

    const colorRng = rngFromSeed(initial.seed);
    const colorResult = generateBlobColorPair(initial.channel, colorRng);
    this.blobColorA = colorResult.pair[0];
    this.blobColorB = colorResult.pair[1];
    // Seed the store's hue picker fields with the seed-derived
    // complementary pair so the controls panel comes up with the
    // correct initial values (state.ts only holds neutral placeholders).
    // No subscribers are attached yet — start() wires them — so this is
    // a quiet field write.
    //
    // Halftone hues are seeded from the same Channel pair on first
    // load so the very first paint reads as a coordinated palette;
    // after that they persist across channel switches and only
    // re-seed on Reset (= new seed). See handleStateChange below.
    store.set({
      hueA: colorResult.seedHues.hueA,
      hueB: colorResult.seedHues.hueB,
      halftoneHueOne: colorResult.seedHues.hueA,
      halftoneHueTwoA: colorResult.seedHues.hueA,
      halftoneHueTwoB: colorResult.seedHues.hueB,
    });

    // Empty payload to start with. Will be populated as blobs are added.
    this.hotspotPayload = serializeForShader([]);
    this.hotspotPayloadView = {
      positions: this.hotspotPayload.positions,
      colorsLCH: this.hotspotPayload.colors,
      intensities: this.hotspotPayload.intensities,
      blobIds: this.hotspotPayload.blobIds,
      activeCount: this.hotspotPayload.activeCount,
    };

    const renderAdapter: RenderAdapter = {
      renderToCanvas: async (w, h) => {
        this.pushUniformsForCurrentFrame(performance.now() / 1000);
        return this.renderer.renderToCanvas(w, h);
      },
    };
    const typographyAdapter: TypographyAdapter = {
      renderToCanvas: ({
        width,
        height,
        layout,
        title,
        date,
        time,
        titleSizePx,
        titleWeight,
        orientation,
      }) =>
        renderTypographyToCanvas({
          width,
          height,
          layout,
          title,
          date,
          time,
          titleSizePx,
          titleWeight,
          orientation,
        }),
    };
    this.pngExporter = createPngExporter({
      render: renderAdapter,
      typography: typographyAdapter,
    });

    // The MediaRecorder is rebuilt per recording session in
    // `handleToggleRecording` so the current bgMedia audio (if any)
    // can be mixed in alongside the Tone.js master output.

    // Phase 5a — metallic spawn sound (PoC). Built up-front so the
    // synth chain is ready, but no audio plays until the first user
    // gesture resumes the AudioContext via Tone.start() (called
    // lazily inside `spawnSound.play()`).
    this.spawnSound = new SpawnSound(initial.seed);
  }

  start(): void {
    const actions: ControlActions = {
      reset: () => this.handleReset(),
      exportPng: () => this.handleExportPng(),
      toggleRecording: () => this.handleToggleRecording(),
      applyHueColors: () => this.applyColorsToExistingBlobs(),
      getRecordingElapsedSeconds: () => this.webmRecorder?.elapsedSeconds() ?? 0,
    };
    this.controlsHandle = mountControls(this.controlsRoot, actions);

    store.subscribe((next, prev) => this.handleStateChange(next, prev));

    this.applyViewport(store.get().aspect);
    this.syncWorldLineWidthInset();
    this.applyResetForCurrentState();
    void ensureFontsReady().then(() => this.composeTypographyForCurrent());

    this.canvas.addEventListener('pointerdown', this.handlePointerDown);
    window.addEventListener('pointermove', this.handlePointerMove);
    window.addEventListener('pointerup', this.handlePointerUp);

    // Re-acquire the screen Wake Lock when the page returns from
    // background — the OS releases it automatically on visibility change.
    installWakeLockVisibilityHandler();

    window.addEventListener('resize', this.handleResize);

    this.startTime = performance.now();
    this.prevFrameMs = this.startTime;
    this.rafHandle = requestAnimationFrame(this.tick);
  }

  stop(): void {
    if (this.rafHandle !== null) {
      cancelAnimationFrame(this.rafHandle);
      this.rafHandle = null;
    }
    this.canvas.removeEventListener('pointerdown', this.handlePointerDown);
    window.removeEventListener('pointermove', this.handlePointerMove);
    window.removeEventListener('pointerup', this.handlePointerUp);
    window.removeEventListener('resize', this.handleResize);
    this.controlsHandle?.unmount();
    this.controlsHandle = null;
    this.renderer.dispose();
    this.spawnSound.dispose();
  }

  // ----- State change ----------------------------------------------------

  private handleStateChange(next: AppState, prev: AppState): void {
    const seedChanged = next.seed !== prev.seed;
    const channelChanged = next.channel !== prev.channel;
    const aspectChanged = next.aspect !== prev.aspect;
    const hueChanged = next.hueA !== prev.hueA || next.hueB !== prev.hueB;
    const titleTypoChanged =
      next.titleWeight !== prev.titleWeight || next.titleSize !== prev.titleSize;
    const titleContentChanged =
      next.titleText !== prev.titleText
      || next.dateText !== prev.dateText
      || next.timeText !== prev.timeText
      || next.titleOrientation !== prev.titleOrientation;
    const bgMediaChanged = next.bgMedia !== prev.bgMedia;

    Object.assign(this.world.params, paramsFromState(next));
    this.syncWorldLineWidthInset();

    const fullscreenChanged = next.isFullscreenMode !== prev.isFullscreenMode;
    if (aspectChanged || fullscreenChanged) this.applyViewport(next.aspect);

    if (next.letterboxFrame !== prev.letterboxFrame) {
      document.body.classList.toggle('kw-letterbox', next.letterboxFrame);
    }

    if (bgMediaChanged) {
      this.renderer.setBackgroundMedia(next.bgMedia);
    }

    if (seedChanged || channelChanged || aspectChanged) {
      if (seedChanged || aspectChanged) {
        this.layout = pickCorners(next.seed, next.aspect);
      }
      // Channel / seed / aspect change: recompute, push to existing
      // blobs (a "world reset" event), AND reset the hue pickers to the
      // new seed-derived complementary pair (case B' in Phase 3 design).
      const result = this.recomputeChannelColors(next.channel, next.seed);
      this.applyColorsToExistingBlobs();
      // Mirror the seed-derived hues into the store so the picker UI
      // reflects the post-switch defaults. This fires a recursive
      // handleStateChange in the hue branch below; the recursion is
      // benign (override === seedHues, so the recompute is a no-op).
      //
      // Halftone hues are an independent layer (Phase 4 fixup):
      //   - Channel switch alone (= colour preset swap) leaves them
      //     alone, since the user may have tuned them deliberately.
      //   - Reset (= seed change = world rebuild) re-seeds them from
      //     the new pair, so the same `?seed=...` URL reproduces the
      //     same halftone palette across reloads.
      const update: Partial<AppState> = {
        hueA: result.seedHues.hueA,
        hueB: result.seedHues.hueB,
      };
      if (seedChanged) {
        update.halftoneHueOne = result.seedHues.hueA;
        update.halftoneHueTwoA = result.seedHues.hueA;
        update.halftoneHueTwoB = result.seedHues.hueB;
        // Phase 5a — refresh the audio scale tonic so the same
        // ?seed=… URL reproduces the same musical palette.
        this.spawnSound.reseed(next.seed);
      }
      store.set(update);
      if (seedChanged || aspectChanged) this.applyResetForCurrentState();
      this.composeTypographyForCurrent();
    } else if (hueChanged) {
      // Picker tweak: recompute the active palette for the next spawn,
      // but leave existing blobs untouched. The user confirms the change
      // explicitly via "Apply to existing blobs".
      this.recomputeChannelColors(next.channel, next.seed, {
        hueA: next.hueA,
        hueB: next.hueB,
      });
    } else if (titleTypoChanged || titleContentChanged) {
      this.composeTypographyForCurrent();
    }
  }

  // ----- Color regeneration ---------------------------------------------

  /**
   * Recompute the channel palette (= colours used for the next spawn)
   * without touching existing blobs. Returns the full result so the
   * caller can read `seedHues` for picker initialisation.
   *
   * Phase 3 split: this used to also reassign every existing blob's
   * colour; that responsibility now lives in `applyColorsToExistingBlobs`
   * and is invoked separately on channel switch and on the explicit
   * "Apply to existing blobs" button.
   */
  private recomputeChannelColors(
    channel: ChannelId,
    seed: string,
    hueOverride?: { hueA: number; hueB: number },
  ): BlobColorPairResult {
    const rng = rngFromSeed(seed);
    const result = generateBlobColorPair(channel, rng, hueOverride);
    this.blobColorA = result.pair[0];
    this.blobColorB = result.pair[1];
    this.world.bumpVersion();
    return result;
  }

  /**
   * Push the current `blobColorA/B` onto every existing blob (A/B
   * alternating in blob age order) and reset `nextColorPick` so the
   * spawn alternation realigns with index 0. Used by:
   *   - the channel/seed/aspect change path (channel switch = world
   *     reset semantics)
   *   - the "Apply to existing blobs" button on the hue picker.
   */
  private applyColorsToExistingBlobs(): void {
    this.nextColorPick = 0;
    for (let i = 0; i < this.world.blobs.length; i++) {
      const blob = this.world.blobs[i];
      if (!blob) continue;
      blob.color = i % 2 === 0 ? this.blobColorA : this.blobColorB;
    }
    this.world.bumpVersion();
  }

  // ----- Reset / spawn --------------------------------------------------

  private applyResetForCurrentState(): void {
    const s = store.get();
    const { width, height } = this.world.bounds;
    const shortEdge = Math.min(width, height);

    this.world.params.attractionMinR = 0.05 * shortEdge;
    this.world.rng = rngFromSeed(s.seed + ':physics');
    this.world.reset();
    this.nextColorPick = 0;
  }

  /**
   * Add a blob at world position with the next alternating channel color.
   * `spawnDuration` is supplied by the caller (computed from objectSize
   * — see Phase 2.5 comment in `handlePointerDown`). The world's
   * `addBlob` keeps a 0.6s fallback for direct/test callers that pass
   * `undefined`.
   */
  private spawnBlob(
    position: [number, number],
    targetRadius: number,
    spawnDuration: number,
  ): void {
    const color = this.nextColorPick % 2 === 0 ? this.blobColorA : this.blobColorB;
    this.nextColorPick++;
    this.world.addBlob({
      position,
      color,
      targetRadius,
      spawnDuration,
      warpSeed: this.world.rng(),
      mass: 1,
    });
    // Phase 5a — fire one metallic note per spawn. Fire-and-forget;
    // the first call lazily resumes the AudioContext (which needs the
    // user gesture this handler is already inside of).
    void this.spawnSound.play(store.get().objectSize);
  }

  // ----- Viewport / aspect ----------------------------------------------

  private applyViewport(aspect: AspectId): void {
    // Hide-UI fullscreen mode bypasses aspect letterboxing so the canvas
    // fills the window edge-to-edge. The capture pipelines still read
    // `state.aspect` directly for export sizing, so PNG/WebM output
    // remains aspect-locked regardless of this preview mode.
    const isFullscreen = store.get().isFullscreenMode;
    let width: number;
    let height: number;
    let dpr: number;
    if (isFullscreen) {
      width = Math.max(1, Math.floor(window.innerWidth));
      // iOS innerHeight excludes the home-indicator area on standalone
      // PWA, leaving a body-coloured strip at the bottom. documentElement
      // .clientHeight reports the largest available viewport in that
      // mode — taking the max of the two reaches edge-to-edge without
      // regressing on browsers where innerHeight is already the larger
      // value.
      height = Math.max(
        1,
        Math.floor(Math.max(window.innerHeight, document.documentElement.clientHeight)),
      );
      dpr = Math.min(Math.max(window.devicePixelRatio || 1, 1), 2);
    } else {
      const viewport = computeViewport(aspect, {
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight,
      });
      width = viewport.width;
      height = viewport.height;
      dpr = viewport.dpr;
    }
    const adapter = this.makeAspectAdapter();
    applyAspect(aspect, { width, height, dpr }, adapter);
    this.canvas.style.position = 'fixed';
    this.canvas.style.left = '50%';
    this.canvas.style.top = '50%';
    this.canvas.style.transform = 'translate(-50%, -50%)';
    this.canvas.style.width = `${width}px`;
    this.canvas.style.height = `${height}px`;
    this.overlay.style.position = 'fixed';
    this.overlay.style.left = '50%';
    this.overlay.style.top = '50%';
    this.overlay.style.transform = 'translate(-50%, -50%)';
    this.overlay.style.width = `${width}px`;
    this.overlay.style.height = `${height}px`;
  }

  private makeAspectAdapter(): AspectAdapter {
    return {
      setRenderSize: (w, h, dpr) => this.renderer.setSize(w, h, dpr),
      setWorldBounds: (bounds) => this.world.setBounds(bounds),
      relayoutTypography: () => this.composeTypographyForCurrent(),
    };
  }

  // ----- Typography ------------------------------------------------------

  private composeTypographyForCurrent(): void {
    const { width, height } = this.world.bounds;
    const s = store.get();
    composeTypography({
      overlay: this.overlay,
      layout: this.layout,
      title: s.titleText,
      date: s.dateText,
      time: s.timeText,
      canvasShortEdge: Math.min(width, height),
      titleSizePx: s.titleSize,
      titleWeight: s.titleWeight,
      orientation: s.titleOrientation,
    });
    this.typographyMounted = true;
    // Phase 8 — invalidate the cached recording-composite typography
    // canvas so the next captured frame picks up the new text layout.
    this.invalidateTypographyOverlayCanvas();
  }

  // ----- Reset / export / record ----------------------------------------

  private handleReset(): void {
    const newSeed = generateSeed();
    // Mirror the seed into the URL so a refresh / share / screenshot reproduces
    // the same start-state. replaceState (not pushState) keeps the back button
    // tied to the user's actual navigation history.
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      url.searchParams.set('seed', newSeed);
      window.history.replaceState({}, '', url.toString());
    }
    store.set({ seed: newSeed });
  }

  private async handleExportPng(): Promise<void> {
    if (!this.controlsHandle) return;
    const s = store.get();
    const resolution = this.controlsHandle.getResolution();
    await ensureFontsReady();
    const { blob, filename } = await this.pngExporter.export({
      aspect: s.aspect,
      channel: s.channel,
      seed: s.seed,
      resolution,
      layout: this.layout,
      title: s.titleText,
      date: s.dateText,
      time: s.timeText,
      titleSizePx: s.titleSize,
      titleWeight: s.titleWeight,
      orientation: s.titleOrientation,
    });
    downloadBlob(blob, filename);
  }

  private async handleToggleRecording(): Promise<void> {
    if (!this.controlsHandle) return;
    const s = store.get();
    if (this.webmRecorder !== null && this.webmRecorder.isRecording()) {
      const result = await this.webmRecorder.stop();
      // Drop the SpawnSound's MediaStreamDestination so the next
      // recording wires up a fresh tap (and the speaker path stays
      // clean between sessions).
      this.spawnSound.releaseRecordStream();
      this.webmRecorder = null;
      this.controlsHandle.setRecording(false);
      downloadBlob(result.blob, result.filename);
    } else {
      // Collect audio streams to mix into the recording. Order isn't
      // significant — MediaRecorder muxes all audio tracks present on
      // the stream into the opus track.
      const audioStreams: MediaStream[] = [];
      // 1. Tone.js master (marimba spawn / fusion sounds).
      audioStreams.push(this.spawnSound.getRecordStream());
      // 2. Background video audio, if a video is currently bound.
      //    `HTMLMediaElement.captureStream()` returns a stream with
      //    audio tracks; we only consume the audio side downstream
      //    (the canvas already carries the composited picture).
      const media = s.bgMedia;
      if (media instanceof HTMLVideoElement) {
        const cap =
          (media as HTMLVideoElement & { captureStream?: () => MediaStream }).captureStream ??
          (media as HTMLVideoElement & { mozCaptureStream?: () => MediaStream }).mozCaptureStream;
        if (typeof cap === 'function') {
          try {
            const mediaStream = cap.call(media);
            if (mediaStream.getAudioTracks().length > 0) {
              audioStreams.push(mediaStream);
            }
          } catch (err) {
            console.warn('[recording] failed to tap bg-video audio:', err);
          }
        }
      }
      // Phase 8 — record from the composite canvas (WebGL frame +
      // typography overlay) instead of the bare WebGL canvas, so the
      // muxed WebM includes the title/date/time text. The composite
      // canvas is filled inside the tick loop while recording is
      // active.
      const compositeCanvas = this.ensureCompositeCanvas();
      // Debug log: snapshot the audio inputs gathered for this
      // recording session — Tone master should always contribute one
      // track, the bgVideo audio is conditional. If totalAudioTracks
      // is 0 here, the WebM will end up silent. Remove with
      // `[record-debug]` grep when verification is complete.
      console.log(
        '[record-debug] audioStreams:',
        'count=' + audioStreams.length,
        'totalAudioTracks='
          + audioStreams.reduce((n, st) => n + st.getAudioTracks().length, 0),
        'hasBgVideo=' + (s.bgMedia instanceof HTMLVideoElement),
      );
      this.webmRecorder = createWebmRecorder({ canvas: compositeCanvas, audioStreams });
      this.webmRecorder.start({ aspect: s.aspect, channel: s.channel, seed: s.seed });
      this.controlsHandle.setRecording(true);
    }
  }

  // ----- Recording composite layer (Phase 8) ----------------------------

  /**
   * Return the lazily-allocated composite canvas. Size is synced to the
   * WebGL canvas's current intrinsic size on every call so a mid-session
   * aspect/DPR change is picked up the next frame. The canvas is
   * detached from the DOM (captureStream works without it being in the
   * document tree).
   */
  private ensureCompositeCanvas(): HTMLCanvasElement {
    if (this.compositeCanvas === null) {
      const c = document.createElement('canvas');
      this.compositeCanvas = c;
      const ctx = c.getContext('2d');
      if (ctx === null) {
        throw new Error('Phase 8: failed to acquire 2D context for composite canvas');
      }
      this.compositeCtx = ctx;
    }
    this.syncCompositeCanvasSize();
    return this.compositeCanvas;
  }

  /** Match the composite canvas dimensions to the WebGL canvas. */
  private syncCompositeCanvasSize(): void {
    const c = this.compositeCanvas;
    if (c === null) return;
    if (c.width !== this.canvas.width) c.width = this.canvas.width;
    if (c.height !== this.canvas.height) c.height = this.canvas.height;
  }

  /**
   * Render (or re-render) the cached typography canvas at the composite
   * canvas's current pixel size. Async because `renderTypographyToCanvas`
   * awaits `ensureFontsReady`. While the render is inflight subsequent
   * calls are no-ops; once it completes, future frames pick up the new
   * canvas via `typographyOverlayCanvas`.
   */
  private requestTypographyOverlayCanvasRender(width: number, height: number): void {
    if (this.typographyOverlayRenderInflight) return;
    this.typographyOverlayRenderInflight = true;
    const s = store.get();
    const layout = this.layout;
    void renderTypographyToCanvas({
      width,
      height,
      layout,
      title: s.titleText,
      date: s.dateText,
      time: s.timeText,
      titleSizePx: s.titleSize,
      titleWeight: s.titleWeight,
      orientation: s.titleOrientation,
    })
      .then((canvas) => {
        this.typographyOverlayCanvas = canvas;
      })
      .catch((err) => {
        console.warn('[recording] typography overlay render failed:', err);
      })
      .finally(() => {
        this.typographyOverlayRenderInflight = false;
      });
  }

  /**
   * Drop the cached typography overlay canvas. Called whenever the
   * inputs that feed it change (title / date / time text, orientation,
   * layout, aspect, weight, size). The next composite frame will
   * trigger a fresh render.
   */
  private invalidateTypographyOverlayCanvas(): void {
    this.typographyOverlayCanvas = null;
  }

  /**
   * Per-frame composite step. Only runs while recording is active so
   * the non-recording path stays zero-cost. Blits the WebGL frame onto
   * the composite canvas, then drawImage's the cached typography
   * overlay (rendering it on demand if invalidated). The composite's
   * captureStream feeds the MediaRecorder.
   */
  private updateCompositeFrame(): void {
    const ctx = this.compositeCtx;
    const composite = this.compositeCanvas;
    if (ctx === null || composite === null) return;
    this.syncCompositeCanvasSize();
    const w = composite.width;
    const h = composite.height;
    if (w === 0 || h === 0) return;
    ctx.clearRect(0, 0, w, h);
    ctx.drawImage(this.canvas, 0, 0, w, h);
    // Render typography on demand (matched to current composite size).
    if (
      this.typographyOverlayCanvas === null
      || this.typographyOverlayCanvas.width !== w
      || this.typographyOverlayCanvas.height !== h
    ) {
      this.requestTypographyOverlayCanvasRender(w, h);
    }
    const overlay = this.typographyOverlayCanvas;
    if (overlay !== null && overlay.width === w && overlay.height === h) {
      ctx.drawImage(overlay, 0, 0, w, h);
    }
  }

  // ----- Mouse -----------------------------------------------------------

  private handlePointerDown = (e: PointerEvent): void => {
    // First-gesture side effects:
    //  1. Unmute the bg-media video if one is bound. iOS Safari
    //     requires `muted=true` for autoplay; this promotes the kiosk-
    //     mode silent loop into an audible playback at the moment
    //     the visitor first touches the canvas. Idempotent — no-op
    //     if already unmuted.
    //  2. Request a screen Wake Lock so the iPad doesn't dim or sleep
    //     during the exhibition. Idempotent; non-fatal on unsupported
    //     browsers (no-op).
    const media = store.get().bgMedia;
    if (media instanceof HTMLVideoElement) {
      if (media.muted) media.muted = false;
      media.play().catch(() => {
        /* play() can reject if the element is detached; not fatal. */
      });
    }
    void requestWakeLock();

    const worldPos = this.cssToWorld(e.clientX, e.clientY);
    if (!worldPos) return;
    const idx = this.world.pickBlob(worldPos);
    if (idx !== null) {
      // Hit an existing blob → drag it.
      const blob = this.world.blobs[idx];
      if (!blob) return;
      this.dragBlobId = blob.id;
      this.prevMouseWorld = worldPos;
      this.canvas.setPointerCapture(e.pointerId);
      return;
    }
    // Empty space → spawn a new blob at the click point. No overlap check;
    // the surface-tension spring's repulsive branch handles deep overlap,
    // and clusters of newly-stacked blobs are now a supported pattern.
    const s = store.get();
    const shortEdge = Math.min(this.world.bounds.width, this.world.bounds.height);
    const targetRadius = clamp(s.objectSize, 0.15, 0.45) * shortEdge;

    // Phase 2.5: spawn duration scales with objectSize so larger blobs
    // settle in more slowly. The curve has been tuned twice: a sqrt
    // law (exp 0.5, ~1.73× spread) felt too subtle, then exp 0.7
    // (~2.16× spread) still didn't read as a meaningful weight cue.
    // This iteration goes super-linear (exp 1.2) to push the dynamic
    // range to ~3.7× across the slider — large blobs now read as
    // distinctly heavier water droplets that take their time to form.
    //
    //   spawnDuration = clamp(1.38 * (objectSize / 0.3)^1.2, 0.3, 2.5)
    //
    // - The 1.2 exponent (super-linear) has no clean physical
    //   derivation; it is an art-directed value chosen to maximize
    //   the "the bigger, the slower" perceptual effect. Inertia-
    //   dominated physics would give an exp ≤ 1, but in our preview
    //   the steeper curve reads as more deliberate / more droplet-
    //   like, which is the directorial goal.
    // - 1.38s base back-solved so that at the small end of the
    //   slider (s = 0.15) the duration lands near 0.6s — small
    //   enough to feel responsive but still long enough that the
    //   spawn animation is clearly visible.
    // - s_ref = 0.3 sits near the middle of the current objectSize
    //   slider; at s = 0.3 the formula yields exactly the base
    //   (1.38s), with the rest of the curve falling out from there.
    //   (This breaks strict back-compat with the pre-Phase-2.5
    //   hardcoded 0.6s — intentional.)
    // - Lower clamp 0.3s: at 60fps with the dt cap of 33ms (`tick`
    //   above), 0.3s is ~18 frames. The Phase 1 cubic spawn gate
    //   (spawnInfluence = spawnT^3) still ramps across enough samples
    //   at this floor to avoid impulsive force engagement. (Within
    //   today's slider range the lower clamp is never reached — the
    //   curve bottoms out at ~0.60s — but it remains as a guard.)
    // - Upper clamp 2.5s: today's [0.15, 0.45] range peaks at ~2.23s
    //   (s = 0.45). Headroom for any future range expansion.
    const spawnDuration = clamp(1.38 * Math.pow(s.objectSize / 0.3, 1.2), 0.3, 2.5);

    this.spawnBlob(worldPos, targetRadius, spawnDuration);
  };

  private handlePointerMove = (e: PointerEvent): void => {
    if (this.dragBlobId === null) return;
    const idx = this.world.findBlobIndexById(this.dragBlobId);
    if (idx === null) {
      // Blob was absorbed/evicted while we were dragging — stop.
      this.dragBlobId = null;
      this.prevMouseWorld = null;
      return;
    }
    const world = this.cssToWorld(e.clientX, e.clientY);
    if (!world || !this.prevMouseWorld) return;
    const dx = world[0] - this.prevMouseWorld[0];
    const dy = world[1] - this.prevMouseWorld[1];
    const dt = Math.max(1 / 240, this.world.getLastDt());
    this.world.applyMouseDrag(idx, [dx / dt, dy / dt]);
    this.prevMouseWorld = world;
  };

  private handlePointerUp = (_e: PointerEvent): void => {
    this.dragBlobId = null;
    this.prevMouseWorld = null;
  };

  private cssToWorld(clientX: number, clientY: number): [number, number] | null {
    const rect = this.canvas.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return null;
    const xCss = clientX - rect.left;
    const yCss = clientY - rect.top;
    const cx = xCss - rect.width / 2;
    const cy = -(yCss - rect.height / 2);
    const sx = this.world.bounds.width / rect.width;
    const sy = this.world.bounds.height / rect.height;
    return [cx * sx, cy * sy];
  }

  // ----- Resize ----------------------------------------------------------

  private handleResize = (): void => {
    this.applyViewport(store.get().aspect);
    if (this.typographyMounted) this.composeTypographyForCurrent();
    // Phase 8 — composite canvas tracks the WebGL canvas; size diff
    // is picked up on the next composite frame, but we proactively
    // invalidate the typography overlay so the next-frame render
    // matches the new dimensions.
    this.invalidateTypographyOverlayCanvas();
  };

  // ----- Animation loop --------------------------------------------------

  private tick = (nowMs: number): void => {
    const rawDt = (nowMs - this.prevFrameMs) / 1000;
    const dt = Math.min(1 / 30, Math.max(0, rawDt));
    this.prevFrameMs = nowMs;
    const time = (nowMs - this.startTime) / 1000;

    this.world.step(dt);

    // Phase 5b — detect new blob-pair fusions and trigger chords.
    // Runs after physics has settled this frame's positions but
    // before render, reading positions/radii read-only. Audio
    // concerns stay in the orchestration layer; physics is
    // untouched.
    this.detectFusionEvents();

    this.maybeRebuildHotspotPayload();
    this.pushUniformsForCurrentFrame(time);
    this.renderer.render();

    // Phase 8 — update the recording composite (WebGL + text) only
    // while a recording session is live. Skipping this when idle
    // keeps the non-recording hot path zero-cost.
    if (this.webmRecorder !== null && this.webmRecorder.isRecording()) {
      this.updateCompositeFrame();
    }

    this.rafHandle = requestAnimationFrame(this.tick);
  };

  /**
   * Phase 5b — scan every fully-spawned blob pair, fire a fusion
   * chord for any pair that has just entered the deep-contact zone
   * (`distance < radSum × 0.7`). Pairs that are still in contact
   * from a previous frame don't re-trigger; pairs that separate are
   * removed from the tracking set and become eligible to fire again
   * if they re-collide.
   *
   * The 0.7 threshold (rather than 1.0 = first kiss) keeps chords
   * rare in dense clusters — they read as punctuation in the
   * impressionistic soundscape, not as constant chatter.
   *
   * Runs O(N²) over up to MAX_BLOBS=10 → 45 pairs × 60Hz = ~2700
   * ops/sec. Negligible.
   */
  private detectFusionEvents(): void {
    const blobs = this.world.blobs;
    const shortEdge = Math.min(
      this.world.bounds.width,
      this.world.bounds.height,
    );
    const nowInContact = new Set<string>();
    for (let i = 0; i < blobs.length; i++) {
      const a = blobs[i];
      if (!a || a.spawnT < 1) continue;
      for (let j = i + 1; j < blobs.length; j++) {
        const b = blobs[j];
        if (!b || b.spawnT < 1) continue;
        const dx = b.position[0] - a.position[0];
        const dy = b.position[1] - a.position[1];
        const dist = Math.hypot(dx, dy);
        const radSum = a.radius + b.radius;
        if (dist >= radSum * 0.7) continue;

        // Sorted-id key so {a,b} and {b,a} collapse to one entry.
        const key = a.id < b.id ? `${a.id}|${b.id}` : `${b.id}|${a.id}`;
        nowInContact.add(key);

        if (!this.mergedPairs.has(key)) {
          // First entry into the contact zone for this pair → chord.
          // SpawnSound expects objectSize as a fraction of the short
          // edge (matches the Phase 5a pitch-mapping convention).
          const sizeA = a.targetRadius / shortEdge;
          const sizeB = b.targetRadius / shortEdge;
          this.spawnSound.playFusion(sizeA, sizeB);
        }
      }
    }
    this.mergedPairs = nowInContact;
  }

  /** Rebuild the hotspot payload only when the world's blob list mutates. */
  private maybeRebuildHotspotPayload(): void {
    const w = this.world.getRenderState();
    if (w.version === this.payloadVersion) return;
    this.payloadVersion = w.version;

    const sources = this.world.blobs
      .slice(0, w.blobCount)
      .map((blob, i) => ({ color: blob.color, blobId: i }));
    this.hotspotPayload = serializeForShader(sources);
    this.hotspotPayloadView = {
      positions: this.hotspotPayload.positions,
      colorsLCH: this.hotspotPayload.colors,
      intensities: this.hotspotPayload.intensities,
      blobIds: this.hotspotPayload.blobIds,
      activeCount: this.hotspotPayload.activeCount,
    };
  }

  private pushUniformsForCurrentFrame(time: number): void {
    const s = store.get();
    const w = this.world.getRenderState();
    const shortEdge = Math.min(this.world.bounds.width, this.world.bounds.height);

    // Phase 4 — halftone tint L/C come from the active channel's
    // preset midpoint. Single source of truth: the CHANNELS table in
    // src/color/channels.ts. Mono produces a near-grey tint by design
    // (cRange ≈ [0, 0.02]).
    const def = CHANNELS[s.channel];
    const halftoneTintL = (def.lRange[0] + def.lRange[1]) / 2;
    const halftoneTintC = (def.cRange[0] + def.cRange[1]) / 2;

    const update: RendererUpdateState = {
      time,
      worldRenderState: w,
      hotspotPayload: this.hotspotPayloadView,
      backgroundColor: BACKGROUND_COLOR_SRGB,
      shortEdge,
      lineWidth: s.lineWidth,
      marginWidth: s.marginWidth,
      bgMediaEnabled: s.bgMediaEnabled,
      bgFitMode: s.bgFitMode,
      bgOpacity: s.bgOpacity,
      halftoneEnabled: s.halftoneEnabled,
      halftoneMode: s.halftoneMode,
      halftoneShape: s.halftoneShape,
      halftoneCellSize: s.halftoneCellSize,
      halftoneGap: s.halftoneGap,
      halftoneTintL,
      halftoneTintC,
      halftoneHueOneDeg: s.halftoneHueOne,
      halftoneHueTwoADeg: s.halftoneHueTwoA,
      halftoneHueTwoBDeg: s.halftoneHueTwoB,
    };
    this.renderer.update(update);
  }

  /**
   * Sync the world's container-inset with the current line-width slider.
   * The slider is in CSS px and world coords are also CSS px (set via
   * setBounds(viewport)), so no aspect scaling is needed — the value goes
   * straight in.
   */
  private syncWorldLineWidthInset(): void {
    this.world.lineWidthHalfPx = store.get().lineWidth * 0.5;
  }
}

// --- Helpers -----------------------------------------------------------------

function clamp(x: number, lo: number, hi: number): number {
  return x < lo ? lo : x > hi ? hi : x;
}

const GRAVITY_PIXEL_SCALE = 2.0e8;

function paramsFromState(s: AppState): PhysicsParams {
  return {
    gravity: s.attraction * GRAVITY_PIXEL_SCALE,
    attractionMinR: 54,
    drag: 0.0015,
    // Vibration Decay slider was removed (it had no physics effect; only
    // the shader's slow-ellipse breathing read it). Hardcoded long τ so a
    // freshly-spawned blob's vibEnergy fades over a few seconds.
    vibrationDecayTau: 8.0,
  };
}
